// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — formulario del cuestionario BPMM (prompt 0735, F3)
// Carga el cuestionario BPMM-SIS vigente, agrupa preguntas por pilar y captura las
// respuestas del usuario autenticado (multi-respondedor, prepobladas al recargar).
// Escala verbal de 5 anclas (Nunca…Siempre → 1-5). Espejo del CuestionarioForm de M1.5.
// i18n vía useI18n() · namespace process.madurez.*
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import { BotonDictado } from '../../../../shared/components/BotonDictado';
import {
  fetchCuestionarioBpmm,
  fetchRespuestasBpmm,
  guardarRespuestasBpmm,
  type CuestionarioBpmm,
  type PreguntaBpmm,
  type RespuestaInputBpmm,
  type PilarBpmm,
  type EjeLiderazgoBpmm,
} from '../api-madurez-proceso';

const A1 = '#1F3864';
const A2 = '#2F5496';
const CE = '#33BCE1';
const GO = '#595959';
const GM = '#D9D9D9';
const W = '#FFF';

const PILAR_ORDER: PilarBpmm[] = [
  'ESTRATEGIA',
  'PROCESOS',
  'METODO',
  'TECNOLOGIA',
  'PERSONAS',
  'GOBIERNO',
  'CULTURA',
];
const PILAR_LABEL: Record<PilarBpmm, string> = {
  ESTRATEGIA: 'Estrategia',
  PROCESOS: 'Procesos',
  METODO: 'Método',
  TECNOLOGIA: 'Tecnología',
  PERSONAS: 'Personas',
  GOBIERNO: 'Gobierno',
  CULTURA: 'Cultura',
};
// Escala verbal de 5 anclas de Robledo (5 = Siempre … 1 = Nunca).
const ANCLA_15: Record<number, string> = {
  1: 'Nunca',
  2: 'Casi nunca',
  3: 'En ocasiones',
  4: 'Casi siempre',
  5: 'Siempre',
};
const EJE_LABEL: Record<EjeLiderazgoBpmm, string> = {
  CONCIENCIA: 'Conciencia',
  ALINEAMIENTO: 'Alineamiento',
  COMPORTAMIENTO: 'Comportamiento',
  ESTILO: 'Estilo',
};
const miniBtn: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 5,
  border: `1px solid ${GM}`,
  background: W,
  color: A2,
  cursor: 'pointer',
  fontFamily: "'DM Sans',sans-serif",
};

interface Props {
  evaluacionId: string;
  onSaved?: () => void;
  /** Evaluación APROBADA → cuestionario congelado, inputs deshabilitados. */
  readOnly?: boolean;
}

export function CuestionarioProcesoForm({ evaluacionId, onSaved, readOnly }: Props) {
  const { t } = useI18n();
  const mk = ((t.process as Record<string, unknown>)?.madurez ?? {}) as Record<string, unknown>;
  const s = (k: string, fb: string): string => (typeof mk[k] === 'string' ? (mk[k] as string) : fb);

  const [cuestionario, setCuestionario] = useState<CuestionarioBpmm | null>(null);
  const [valores, setValores] = useState<Record<string, number>>({});
  const [notas, setNotas] = useState<Record<string, string>>({});
  const [notaOpen, setNotaOpen] = useState<Record<string, boolean>>({});
  // Acordeón por pilar: todos ABIERTOS por defecto (true = cerrado).
  const [pilarCerrado, setPilarCerrado] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCuestionarioBpmm()
      .then((res) => setCuestionario(res.data))
      .catch((err: Error) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!evaluacionId) return;
    let cancelado = false;
    setValores({});
    setNotas({});
    setNotaOpen({});
    setPilarCerrado({});
    setSaved(false);
    setError(null);
    fetchRespuestasBpmm(evaluacionId)
      .then((res) => {
        if (cancelado) return;
        const nv: Record<string, number> = {};
        const nn: Record<string, string> = {};
        const no: Record<string, boolean> = {};
        for (const r of res.data) {
          if (r.valor != null) nv[r.preguntaId] = r.valor;
          if (r.nota != null) {
            nn[r.preguntaId] = r.nota;
            no[r.preguntaId] = true;
          }
        }
        setValores(nv);
        setNotas(nn);
        setNotaOpen(no);
      })
      .catch((err: Error) => {
        if (!cancelado) setError(err.message);
      });
    return () => {
      cancelado = true;
    };
  }, [evaluacionId]);

  const guardar = useCallback(async () => {
    if (!cuestionario) return;
    const respuestas: RespuestaInputBpmm[] = [];
    for (const p of cuestionario.preguntas) {
      const nota = notas[p.id]?.trim() || undefined;
      if (valores[p.id] !== undefined || nota) {
        respuestas.push({ preguntaId: p.id, valor: valores[p.id] ?? null, nota });
      }
    }
    if (respuestas.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await guardarRespuestasBpmm(evaluacionId, respuestas);
      setSaved(true);
      onSaved?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [cuestionario, valores, notas, evaluacionId, onSaved]);

  if (error && !cuestionario) {
    return <div style={{ color: '#7B0000', fontSize: 12, padding: 8 }}>{error}</div>;
  }
  if (!cuestionario) {
    return (
      <div style={{ color: GO, fontSize: 12, padding: 8 }}>
        {s('cuestionarioLoading', 'Cargando cuestionario BPMM…')}
      </div>
    );
  }

  const renderPregunta = (p: PreguntaBpmm) => (
    <div key={p.id} style={{ padding: '6px 0', borderBottom: '1px solid #F2F2F2' }}>
      <div style={{ fontSize: 12, color: '#000', marginBottom: 4 }}>
        {p.enunciado}
        {p.ejeLiderazgo && (
          <span style={{ fontSize: 10, fontWeight: 700, color: A2, marginLeft: 6 }}>
            · {s('liderazgo', 'Liderazgo')}: {EJE_LABEL[p.ejeLiderazgo]}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const sel = valores[p.id] === n;
          return (
            <button
              key={n}
              disabled={readOnly}
              title={ANCLA_15[n]}
              onClick={() => !readOnly && setValores((prev) => ({ ...prev, [p.id]: n }))}
              style={{
                width: 30,
                height: 26,
                borderRadius: 5,
                border: sel ? `2px solid ${A1}` : `1px solid ${GM}`,
                background: sel ? CE : W,
                color: sel ? A1 : GO,
                fontWeight: 700,
                fontSize: 11,
                cursor: readOnly ? 'default' : 'pointer',
                opacity: readOnly && !sel ? 0.55 : 1,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      {valores[p.id] !== undefined && (
        <div style={{ fontSize: 10, color: A2, marginTop: 3, fontStyle: 'italic' }}>
          {valores[p.id]} — {ANCLA_15[valores[p.id]]}
        </div>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={() => setNotaOpen((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
          style={{
            marginTop: 4,
            fontSize: 10,
            color: A2,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          {(notaOpen[p.id] ? '− ' : '+ ') + s('anadirNota', 'Añadir nota')}
        </button>
      )}
      {notaOpen[p.id] && (
        <>
          <textarea
            value={notas[p.id] ?? ''}
            onChange={(e) => setNotas((prev) => ({ ...prev, [p.id]: e.target.value }))}
            maxLength={1000}
            rows={2}
            readOnly={readOnly}
            style={{
              width: '100%',
              marginTop: 4,
              fontSize: 12,
              fontFamily: "'DM Sans',sans-serif",
              border: `1px solid ${GM}`,
              borderRadius: 6,
              padding: 6,
              resize: 'vertical',
              background: readOnly ? '#F7F7F7' : W,
              color: readOnly ? GO : '#000',
            }}
          />
          {!readOnly && (
            <BotonDictado
              value={notas[p.id] ?? ''}
              onChange={(v) => setNotas((prev) => ({ ...prev, [p.id]: v }))}
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {readOnly && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: A1,
            background: '#EAF6FB',
            border: `1px solid ${CE}`,
            borderRadius: 6,
            padding: '6px 10px',
          }}
        >
          🔒 {s('lecturaSolo', 'Evaluación aprobada · cuestionario congelado (solo lectura)')}
        </div>
      )}
      <div style={{ fontSize: 11, color: GO }}>
        {s(
          'cuestIntro',
          'Responde según tu percepción (1 = Nunca, 5 = Siempre). El resultado se contrasta con la lectura real del mapa de procesos.',
        )}
      </div>
      {/* Cabecera: expandir/contraer bloques + notas cualitativas */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={() => setPilarCerrado({})} style={miniBtn}>
          ▾ {s('expandirBloques', 'Expandir bloques')}
        </button>
        <button
          type="button"
          onClick={() => setPilarCerrado(Object.fromEntries(PILAR_ORDER.map((p) => [p, true])))}
          style={miniBtn}
        >
          ▸ {s('contraerBloques', 'Contraer bloques')}
        </button>
        <button
          type="button"
          onClick={() =>
            setNotaOpen(Object.fromEntries(cuestionario.preguntas.map((p) => [p.id, true])))
          }
          style={miniBtn}
        >
          + {s('expandirNotas', 'Expandir notas')}
        </button>
        <button type="button" onClick={() => setNotaOpen({})} style={miniBtn}>
          − {s('contraerNotas', 'Contraer notas')}
        </button>
      </div>
      {PILAR_ORDER.map((pilar) => {
        const preguntas = cuestionario.preguntas
          .filter((p) => p.pilar === pilar)
          .sort((a, b) => a.orden - b.orden);
        if (preguntas.length === 0) return null;
        const abierta = !pilarCerrado[pilar];
        return (
          <div key={pilar}>
            <button
              type="button"
              onClick={() => setPilarCerrado((prev) => ({ ...prev, [pilar]: !prev[pilar] }))}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#F5F8FC',
                border: `1px solid ${GM}`,
                borderRadius: 6,
                padding: '5px 8px',
                marginBottom: abierta ? 4 : 0,
                cursor: 'pointer',
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 10,
                fontWeight: 800,
                color: A2,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 11, color: A1, width: 10 }}>{abierta ? '▾' : '▸'}</span>
              <span style={{ flex: 1 }}>{PILAR_LABEL[pilar]}</span>
              <span style={{ color: GO, fontWeight: 600 }}>{preguntas.length}</span>
            </button>
            {abierta && preguntas.map(renderPregunta)}
          </div>
        );
      })}
      {error && <div style={{ color: '#7B0000', fontSize: 11 }}>{error}</div>}
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={guardar}
            disabled={saving}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: A1,
              color: W,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {saving ? s('guardando', 'Guardando…') : s('guardarRespuestas', 'Guardar respuestas')}
          </button>
          {saved && (
            <span style={{ color: '#16A34A', fontSize: 12, fontWeight: 600 }}>
              {s('respuestasGuardadas', 'Respuestas guardadas')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
